'use client'

import { useCallback, useEffect, useState } from 'react'

// Org operations card: link to the owner-facing dashboard, the location
// roster (via the Payload REST API the admin session already has), and the
// add-location flow that spawns a pre-scoped client for the wizard.

const NICHE_OPTIONS = [
  'salon',
  'beauty',
  'med_spa',
  'barbershop',
  'fitness_studio',
  'cleaning',
  'pest',
  'contractor',
  'landscaping',
  'auto_detailing',
  'coffee',
]

const buttonStyle = (disabled) => ({
  padding: '6px 14px',
  borderRadius: 6,
  border: '1px solid var(--theme-elevation-250)',
  background: disabled ? 'var(--theme-elevation-100)' : 'var(--theme-elevation-0)',
  color: 'inherit',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: 13,
})

const inputStyle = {
  padding: '6px 10px',
  borderRadius: 6,
  border: '1px solid var(--theme-elevation-250)',
  fontSize: 13,
  background: 'var(--theme-elevation-0)',
  color: 'inherit',
}

export function OrgPanelClient({ orgId }) {
  const [locations, setLocations] = useState([])
  const [name, setName] = useState('')
  const [niche, setNiche] = useState('med_spa')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    const res = await fetch(
      `/payload-api/clients?where[organization][equals]=${encodeURIComponent(orgId)}&limit=50&depth=0`,
      { credentials: 'include' },
    )
    if (res.ok) {
      const data = await res.json()
      setLocations(data.docs || [])
    }
  }, [orgId])

  useEffect(() => {
    load()
  }, [load])

  const addLocation = async () => {
    setBusy(true)
    setError('')
    try {
      const res = await fetch('/api/org/add-location', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orgId, name: name.trim(), niche }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) {
        setError(json.error || 'Add location failed')
        return
      }
      window.location.href = json.adminUrl
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      style={{
        border: '1px solid var(--theme-elevation-150)',
        borderRadius: 8,
        padding: '14px 16px',
        background: 'var(--theme-elevation-50)',
        marginBottom: 24,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Organization operations</h3>
        <a href={`/org/${orgId}`} target="_blank" rel="noreferrer" style={{ fontSize: 13, textDecoration: 'underline' }}>
          Open owner dashboard ↗
        </a>
      </div>

      {locations.length > 0 && (
        <ul style={{ listStyle: 'none', margin: '12px 0 0', padding: 0, display: 'grid', gap: 4 }}>
          {locations.map((loc) => (
            <li key={loc.id} style={{ fontSize: 13, display: 'flex', gap: 10, alignItems: 'baseline' }}>
              <a href={`/admin/collections/clients/${loc.id}`} style={{ textDecoration: 'underline' }}>
                {loc.name}
              </a>
              <span style={{ color: 'var(--theme-elevation-500)' }}>
                {loc.niche} · {loc.status}
                {loc.telephony?.phoneNumber ? ` · ${loc.telephony.phoneNumber}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <strong style={{ fontSize: 13 }}>Add location:</strong>
        <input
          placeholder="Location name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={inputStyle}
        />
        <select value={niche} onChange={(e) => setNiche(e.target.value)} style={inputStyle}>
          {NICHE_OPTIONS.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={busy || !name.trim()}
          style={buttonStyle(busy || !name.trim())}
          onClick={addLocation}
        >
          {busy ? 'Creating…' : 'Create & open wizard'}
        </button>
      </div>
      {error && (
        <p style={{ color: 'var(--theme-error-500, #b00020)', fontSize: 13, margin: '8px 0 0' }}>{error}</p>
      )}
    </div>
  )
}
